import { Check, Pencil, Trash2 } from 'lucide-react'
import { ShippingLocation_ } from 'src/api/db/apiShippingLocations'
import { formatAddress } from 'src/shared/components/organizationPage/ShippingLocations/components/utils'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Textarea } from 'src/shared/components/ui/textarea'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { formatContactInfo } from '../utils'

export const ShippingLocationCard = ({
  location,
  onEdit,
  isSelected = false,
  onSelect,
  showItemInstructions = false,
  itemInstructionsValue = '',
  onItemInstructionsChange,
  onDelete,
  deleteActionDisabled = false,
}: {
  location: ShippingLocation_
  onEdit?: (location: ShippingLocation_) => void
  onDelete?: (location: ShippingLocation_) => void
  deleteActionDisabled?: boolean
  isSelected?: boolean
  onSelect?: () => void
  showItemInstructions?: boolean
  itemInstructionsValue?: string
  onItemInstructionsChange?: (value: string) => void
}) => {
  const { t } = useLocalization()
  const contact = formatContactInfo(location)
  const address = formatAddress(location)
  const title =
    address && address !== '-'
      ? address
      : t('Shipping address', 'Leveransadress')
  const meta = [location.company_name, contact].filter(Boolean).join(' • ')

  const itemInstructionsPlaceholder = t(
    'Which items go to this address?',
    'Vilka artiklar ska till den här adressen?'
  )

  return (
    <Column
      className={cn(
        'overflow-hidden rounded-2xl border transition-colors',
        isSelected
          ? 'border-gray-300 bg-gray-50/80'
          : 'border-gray-200 bg-white'
      )}
    >
      <Row className="items-center gap-3 p-4">
        {onSelect ? (
          <button
            type="button"
            onClick={onSelect}
            aria-pressed={isSelected}
            aria-label={t('Select this destination', 'Välj denna destination')}
            className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
              isSelected
                ? 'border-foreground bg-foreground'
                : 'border-gray-300 bg-white'
            )}
          >
            {isSelected ? (
              <Check className="h-3 w-3 text-white" strokeWidth={3} />
            ) : null}
          </button>
        ) : null}

        <button
          type="button"
          onClick={onSelect}
          disabled={!onSelect}
          className="min-w-0 flex-1 text-left"
        >
          <Column className="gap-0.5">
            <Row className="items-center gap-2">
              <span className="truncate text-sm font-medium text-foreground">
                {title}
              </span>
              {location.default ? (
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-muted-foreground">
                  {t('Default', 'Standard')}
                </span>
              ) : null}
            </Row>
            {meta ? (
              <p className="truncate text-sm text-muted-foreground">{meta}</p>
            ) : null}
          </Column>
        </button>

        <Row className="shrink-0 items-center gap-0.5">
          {onEdit ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(location)}
              aria-label={t('Edit location', 'Redigera plats')}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {onDelete ? (
            <Button
              variant="ghost"
              size="icon"
              disabled={deleteActionDisabled}
              onClick={() => onDelete(location)}
              aria-label={t('Delete location', 'Ta bort plats')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </Row>
      </Row>

      {showItemInstructions && isSelected ? (
        <Column className="border-t border-gray-100 px-4 pb-4 pt-3">
          <Textarea
            placeholder={itemInstructionsPlaceholder}
            aria-label={itemInstructionsPlaceholder}
            value={itemInstructionsValue}
            onChange={(e) => onItemInstructionsChange?.(e.target.value)}
            className="min-h-[72px]"
          />
        </Column>
      ) : null}
    </Column>
  )
}
